/**
 * Football 101's on-field guides never sit on a face (QA round 4: the reached cone across the Rookie's chin, BLUE LINE /
 * WAIT FOR GO over his hair, END ZONE on Big Tom's helmet). Two tools, both pure (WorldLayer applies them per frame):
 *
 *   - `dodgeFaces`: a tag (line / yard / end-zone / cone label) that lands on either head steps off it — along its
 *     line when it has one (`slope`), down onto the turf under the heads or up above them, whichever is free (the
 *     coach panel, the screen edges and the other head count as taken), keeping the side it chose while it still
 *     needs one (no flicker as the heads move);
 *   - `headHoles`: the turf marks themselves (cones, zones, arrows, the ring, the line glows) are drawn BEHIND the
 *     heads: a CSS mask with a hole per head, so a head standing on a mark covers it like a sprite would.
 */
import type { Box } from './panel-cover';

/** A head on screen: `y` = the top of the head (TeachFrame.head), `r` = its radius (CSS px). */
export interface FaceHead {
  x: number;
  y: number;
  r: number;
  on: boolean;
}

/** The box a head's art covers (hair and helmet reach a little past the collision radius). */
export const faceBox = (h: FaceHead, extraTop = 0): Box => ({
  l: h.x - h.r * 1.12,
  r: h.x + h.r * 1.12,
  t: h.y - h.r * 0.12 - extraTop,
  b: h.y + h.r * 2.15,
});

const hits = (a: Box, b: Box): boolean => a.r > b.l && a.l < b.r && a.b > b.t && a.t < b.b;
const moved = (b: Box, dx: number, dy: number): Box => ({
  l: b.l + dx,
  r: b.r + dx,
  t: b.t + dy,
  b: b.b + dy,
});

export type DodgeDir = 'up' | 'down';

export interface Dodge {
  dx: number;
  dy: number;
  /** The side it stepped to (null = it sat clear already). */
  dir: DodgeDir | null;
}

/**
 * Where a tag box must move so it covers no face. `slope` = dx per dy of its line (0 = straight up / down);
 * `avoid` = other screen parts it must not land on (the coach panel); `prev` = the side it took last frame
 * (kept while it is still free: no flip-flop). `screenH` bounds the move. Falls back to the preferred side,
 * clamped on screen, when neither side is free.
 */
export const dodgeFaces = (
  box: Box,
  faces: readonly Box[],
  avoid: readonly Box[],
  screenH: number,
  prefer: DodgeDir,
  slope = 0,
  prev: DodgeDir | null = null,
  margin = 8,
): Dodge => {
  if (!faces.some((f) => hits(box, f))) return { dx: 0, dy: 0, dir: null };
  const side = (dir: DodgeDir): { dy: number; ok: boolean } => {
    let dy = 0;
    // step past every face in the way (two heads side by side, one a little higher)
    for (let k = 0; k < 4; k++) {
      const b = moved(box, dy * slope, dy);
      const f = faces.find((x) => hits(b, x));
      if (!f) break;
      dy = dir === 'up' ? Math.min(dy, f.t - margin - box.b) : Math.max(dy, f.b + margin - box.t);
    }
    const b = moved(box, dy * slope, dy);
    const ok =
      !faces.some((x) => hits(b, x)) && b.t >= 4 && b.b <= screenH - 4 && !avoid.some((a) => hits(b, a));
    return { dy, ok };
  };
  const want = prev ?? prefer;
  const other: DodgeDir = want === 'up' ? 'down' : 'up';
  const a = side(want);
  const b = side(other);
  // the kept / preferred side, unless only the other is free (or, when nothing was kept yet, the other is a far
  // shorter move)
  let dir: DodgeDir = want;
  if (!a.ok && b.ok) dir = other;
  else if (a.ok && b.ok && !prev && Math.abs(b.dy) < Math.abs(a.dy) * 0.5) dir = other;
  let dy = dir === want ? a.dy : b.dy;
  if (!a.ok && !b.ok) {
    // nowhere free: the preferred side, kept on screen
    dir = prefer;
    dy = prefer === want ? a.dy : b.dy;
    if (box.t + dy < 4) dy = 4 - box.t;
    if (box.b + dy > screenH - 4) dy = screenH - 4 - box.b;
  }
  return { dx: dy * slope, dy, dir };
};

/** The circle a head covers on screen: centre and radius (CSS px). */
export const headDisc = (h: FaceHead): { x: number; y: number; r: number } => ({
  x: h.x,
  y: h.y + h.r * 1.05,
  r: h.r * 1.08,
});

/**
 * A CSS mask (for a full-screen layer) with a hole per head, or '' when none of `marks` (screen boxes of the turf
 * marks drawn in that layer) reaches a head: the masked layer is only composited when it matters.
 */
export const headHoles = (
  heads: readonly FaceHead[],
  marks: readonly Box[],
  origin: { x: number; y: number } = { x: 0, y: 0 },
): string => {
  const discs = heads.filter((h) => h.on && h.r > 0).map(headDisc);
  const near = discs.filter((d) =>
    marks.some((m) => hits(m, { l: d.x - d.r, r: d.x + d.r, t: d.y - d.r, b: d.y + d.r })),
  );
  return near
    .map(
      (d) =>
        `radial-gradient(circle ${d.r.toFixed(1)}px at ${(d.x - origin.x).toFixed(1)}px ${(d.y - origin.y).toFixed(1)}px, transparent 97%, #000 100%)`,
    )
    .join(', ');
};
